import {
  DealCategory,
  ProductDataInput,
  productDataSchema,
  ProductType,
} from "@/core";
import { Product } from "@/core/entities/product";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm, UseFormProps } from "react-hook-form";

interface UseProductFormProps extends Omit<UseFormProps<ProductDataInput>, "mode"> {
  product?: Product | null;
  mode: "create" | "edit";
}

export function useProductForm({
  product,
  mode,
  ...props
}: UseProductFormProps) {
  const defaultValues: ProductDataInput = useMemo(
    () => ({
      name: "",
      category: DealCategory.AirlineMiles,
      logo: "",
      buyOrSell: ProductType.BOTH,
      minimumValueForSell: 0,
      maximumValueForSell: 0,
      minimumValueForBuy: 0,
      maximumValueForBuy: 0,
      sellPrices: [{ minRange: 0, maxRange: 0, price: 0 }],
      buyPrices: [{ minRange: 0, maxRange: 0, price: 0 }],
      statusTiers: [],
      inactive: false,
      paymentOption: undefined,
      specialAttributeIds: [],
    }),
    [],
  );

  const form = useForm<ProductDataInput>({
    ...props,
    resolver: zodResolver(productDataSchema),
    defaultValues: props.defaultValues || defaultValues,
  });

  // Reset form when product changes (for edit mode)
  const { reset } = form;

  useEffect(() => {
    if (mode === "edit" && product) {
      reset({
        name: product.name || "",
        productCode: product.productCode ?? "",
        category: product.category ?? DealCategory.AirlineMiles,
        logo: product.logo || "",
        buyOrSell: (product.buyOrSell as ProductType) || ProductType.BOTH,
        minimumValueForSell: product.minimumValueForSell || 0,
        maximumValueForSell: product.maximumValueForSell || 0,
        minimumValueForBuy: product.minimumValueForBuy || 0,
        maximumValueForBuy: product.maximumValueForBuy || 0,
        sellPrices:
          product.sellPrices && product.sellPrices.length > 0
            ? product.sellPrices.map((price) => ({
                minRange: price.minRange || 0,
                maxRange: price.maxRange || 0,
                price: price.price || 0,
              }))
            : [{ minRange: 0, maxRange: 0, price: 0 }],
        buyPrices:
          product.buyPrices && product.buyPrices.length > 0
            ? product.buyPrices.map((price) => ({
                minRange: price.minRange || 0,
                maxRange: price.maxRange || 0,
                price: price.price || 0,
              }))
            : [{ minRange: 0, maxRange: 0, price: 0 }],
        statusTiers:
          product.statusTiers && product.statusTiers.length > 0
            ? product.statusTiers.map((tier) => ({
                name: tier.name || "",
                percentage: tier.percentage || 0,
              }))
            : [],
        inactive: product.inactive || false,
        paymentOption: product.paymentOption ?? undefined,
        usernameLabelOverride: product.usernameLabelOverride ?? "",
        emailLabelOverride: product.emailLabelOverride ?? "",
        passwordLabelOverride: product.passwordLabelOverride ?? "",
        specialAttributeIds: product.specialAttributeIds ?? [],
      });
    } else if (mode === "create") {
      reset(defaultValues);
    }
  }, [product, mode, reset, defaultValues]);

  return form;
}
